import { eq, useLiveQuery } from '@tanstack/react-db';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import type { FilterFn } from '@tanstack/table-core';
import { Plus, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  DataTable,
  type DataTableFeatures,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { ProductThumbnail } from '@/components/product-thumbnail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import {
  type BrandRecord,
  brandsCollection,
  type CategoryRecord,
  categoriesCollection,
  type ProductRecord,
  productsCollection,
  type VariantRecord,
  variantsCollection,
} from '@/lib/collections';
import { supabase } from '@/lib/supabase';

/**
 * The catalog list.
 *
 * Three libraries, each doing one job:
 *
 *   - **TanStack DB** syncs products, brands, categories and variants from
 *     Supabase over PostgREST and keeps them live. Flat claim-RLS is what
 *     decides which rows arrive, so there is no tenant filter in the fetch --
 *     see `docs/authorization.md`.
 *   - **TanStack Router** owns when that happens: the loader preloads the four
 *     collections, so arriving here shows rows rather than a skeleton that
 *     resolves a beat later.
 *   - **TanStack Table** models sorting, filtering and paging over rows that
 *     are already in memory, which is why typing in the filter box is instant.
 *
 * shadcn provides the markup, through the shared `DataTable` shell, so this
 * reads the same as the team members table.
 */
export const Route = createFileRoute('/products/')({
  /**
   * Warm the collections before the component mounts.
   *
   * `preload()` resolves once the first sync lands and is idempotent, so
   * revisiting the route is free. The catch is deliberate: a preload failure
   * is not a reason to fail the navigation -- the live query below reports it
   * in place, where the operator can see which part is missing.
   */
  loader: async () => {
    await Promise.all([
      productsCollection.preload(),
      brandsCollection.preload(),
      categoriesCollection.preload(),
      variantsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: ProductsPage,
});

/** What the table renders: a product flattened against its lookups. */
interface ProductRow {
  id: string;
  name: string;
  slug: string;
  status: ProductRecord['status'];
  brand: string;
  category: string;
  priceCents: number | null;
  thc: number | null;
  cbd: number | null;
  sku: string;
  /** First image; a product may carry several, the list shows one. */
  imageUrl: string | null;
}

const column = dataTableColumnHelper<ProductRow>();

const STATUSES = ['active', 'draft', 'archived'] as const;

/**
 * Free text matches what an operator would actually type: a name, a slug, a
 * brand, or a SKU read off a label.
 *
 * A function rather than one of the built-in named filters, because the stock
 * ones test a single column and this has to span four. Applied per row, so the
 * `columnId` argument is ignored on purpose -- global filtering calls this once
 * per column otherwise, and matching the same row four times is wasted work.
 */
const matchesSearch: FilterFn<DataTableFeatures, ProductRow> = (row, _columnId, value) => {
  const term = String(value ?? '')
    .trim()
    .toLowerCase();

  if (!term) return true;

  const { name, slug, brand, sku } = row.original;

  return (
    name.toLowerCase().includes(term) ||
    slug.toLowerCase().includes(term) ||
    brand.toLowerCase().includes(term) ||
    sku.toLowerCase().includes(term)
  );
};

const columns = column.columns([
  column.accessor('name', {
    header: 'Product',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <ProductThumbnail src={row.original.imageUrl} alt={row.original.name} />

        {/* `min-w-0` so the long names in this catalog truncate instead of
            widening the column and pushing everything else off-screen. */}
        <div className="min-w-0">
          <Link
            to="/products/$productId"
            params={{ productId: row.original.id }}
            className="block truncate font-medium hover:underline"
          >
            {row.original.name}
          </Link>
          <div className="text-muted-foreground truncate text-xs">{row.original.slug}</div>
        </div>
      </div>
    ),
  }),

  column.accessor('brand', {
    header: 'Brand',
    cell: ({ getValue }) => <span className="text-sm">{getValue() || '—'}</span>,
  }),

  column.accessor('category', {
    header: 'Category',
    cell: ({ getValue }) => <span className="text-sm">{getValue() || '—'}</span>,
  }),

  column.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();

      return (
        <Badge variant={status === 'active' ? 'default' : 'secondary'} className="capitalize">
          {status}
        </Badge>
      );
    },
  }),

  column.accessor('priceCents', {
    header: 'Price',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => formatPrice(getValue()),
  }),

  column.accessor('thc', {
    header: 'THC',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => formatPercent(getValue()),
  }),

  column.accessor('cbd', {
    header: 'CBD',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => formatPercent(getValue()),
  }),

  column.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row, table }) => (
      <div className="flex justify-end gap-1">
        <Button asChild variant="ghost" size="sm">
          <Link to="/products/$productId" params={{ productId: row.original.id }}>
            Edit
          </Link>
        </Button>

        <PublishButton row={row.original} table={table} />
      </div>
    ),
  }),
]);

function ProductsPage() {
  const { activeAccountId, can } = useAuth();
  const navigate = useNavigate();
  const canEdit = can('catalog.manage');

  const [globalFilter, setGlobalFilter] = useState('');
  const [status, setStatus] = useState<'all' | (typeof STATUSES)[number]>('all');

  const accountId = activeAccountId ?? '';

  /*
   * Four live queries rather than one joined query. The lookups are a few
   * hundred rows each and are wanted as maps anyway, so joining them in the
   * query engine would cost more than indexing them once here -- and it keeps
   * what is pushed to PostgREST simple enough to reason about.
   *
   * The `where` is presentation, not protection: RLS already limited every one
   * of these to accounts in the caller's JWT. It matters only for staff who
   * belong to more than one dispensary.
   */
  const products = useLiveQuery(
    (q) =>
      q
        .from({ product: productsCollection })
        .where(({ product }) => eq(product.account_id, accountId)),
    [accountId],
  );

  const brands = useLiveQuery(
    (q) =>
      q.from({ brand: brandsCollection }).where(({ brand }) => eq(brand.account_id, accountId)),
    [accountId],
  );

  const categories = useLiveQuery(
    (q) =>
      q
        .from({ category: categoriesCollection })
        .where(({ category }) => eq(category.account_id, accountId)),
    [accountId],
  );

  const variants = useLiveQuery(
    (q) =>
      q
        .from({ variant: variantsCollection })
        .where(({ variant }) => eq(variant.account_id, accountId)),
    [accountId],
  );

  const rows = useMemo<ProductRow[]>(() => {
    const brandById = new Map(
      ((brands.data ?? []) as BrandRecord[]).map((brand) => [brand.id, brand.name]),
    );
    const categoryById = new Map(
      ((categories.data ?? []) as CategoryRecord[]).map((category) => [category.id, category.name]),
    );

    // Lowest-positioned variant wins, matching what a storefront shows as the
    // "from" price.
    const variantByProduct = new Map<string, VariantRecord>();

    for (const variant of (variants.data ?? []) as VariantRecord[]) {
      const current = variantByProduct.get(variant.product_id);

      if (!current || (variant.position ?? 0) < (current.position ?? 0)) {
        variantByProduct.set(variant.product_id, variant);
      }
    }

    return ((products.data ?? []) as ProductRecord[]).map((product) => {
      const variant = variantByProduct.get(product.id);

      return {
        id: product.id,
        name: product.name,
        slug: product.slug,
        status: product.status,
        brand: (product.brand_id && brandById.get(product.brand_id)) || '',
        category: (product.category_id && categoryById.get(product.category_id)) || '',
        priceCents: variant?.price_cents ?? null,
        thc: product.thc_percent,
        cbd: product.cbd_percent,
        sku: variant?.sku ?? '',
        imageUrl: product.image_urls?.[0] ?? null,
      };
    });
  }, [products.data, brands.data, categories.data, variants.data]);

  /*
   * The status facet narrows the rows before the table sees them, rather than
   * going through column filtering: it is a single equality over a field the
   * table does not otherwise need to model.
   *
   * The footer is still given the unfaceted count, so "of 1,967 total" always
   * means the catalog. Comparing the page against the facet instead would read
   * "1-1 of 1" with no hint that 1,966 rows were set aside.
   */
  const scoped = useMemo(
    () => (status === 'all' ? rows : rows.filter((row) => row.status === status)),
    [rows, status],
  );

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: scoped,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageIndex: 0, pageSize: 25 } },
    globalFilterFn: matchesSearch,
    meta: { canEdit },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-muted-foreground text-sm">
            {products.isLoading
              ? 'Syncing the catalog…'
              : `${rows.length.toLocaleString()} product${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
            <SelectTrigger className="w-36" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((value) => (
                <SelectItem key={value} value={value} className="capitalize">
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="Filter by name, slug, brand or SKU…"
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="w-72"
          />

          {canEdit && (
            <Button asChild size="sm">
              <Link to="/products/new">
                <Plus className="size-4" />
                New product
              </Link>
            </Button>
          )}
        </div>
      </div>

      <SyncWarning accountId={accountId} synced={rows.length} ready={!products.isLoading} />

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={products.isLoading}
        onRowActivate={(row) =>
          navigate({ to: '/products/$productId', params: { productId: row.id } })
        }
        emptyMessage={
          globalFilter || status !== 'all' ? 'No products match those filters.' : 'No products yet.'
        }
      />

      <DataTablePagination table={table} total={rows.length} />
    </div>
  );
}

/**
 * Warn when the browser holds fewer products than the database has.
 *
 * The collection syncs a whole table in one request, and PostgREST caps a
 * response at `max_rows`. When it truncates it says so nowhere -- the adapter
 * cannot tell a capped response from a complete one, so the list silently drops
 * rows off the end. That is what happened at 1967 products against the default
 * cap of 1000.
 *
 * `config.toml` now sets the cap above the catalog, but a cap is a number
 * somebody has to keep ahead of the data, and the hosted project carries its
 * own. A `head` count costs one round trip and turns a silent wrong answer into
 * a visible one.
 */
function SyncWarning({
  accountId,
  synced,
  ready,
}: {
  accountId: string;
  synced: number;
  ready: boolean;
}) {
  const total = useQuery({
    queryKey: ['products-count', accountId],
    enabled: Boolean(accountId) && ready,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('products')
        .select('id', { count: 'exact', head: true })
        .eq('account_id', accountId);

      if (error) throw error;

      return count ?? 0;
    },
  });

  if (!total.data || synced >= total.data) return null;

  return (
    <div className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-lg border p-3 text-sm">
      <TriangleAlert className="mt-0.5 size-4 shrink-0" />

      <p>
        Showing {synced.toLocaleString()} of {total.data.toLocaleString()} products. The response
        was truncated before it reached the browser — raise <code>max_rows</code> for this project,
        or this list is not the whole catalog.
      </p>
    </div>
  );
}

function PublishButton({
  row,
  table,
}: {
  row: ProductRow;
  table: { options: { meta?: { canEdit?: boolean } } };
}) {
  const canEdit = table.options.meta?.canEdit ?? false;

  /**
   * Optimistic: the row flips immediately and rolls back on its own if Postgres
   * refuses -- which is exactly what RLS does for a member without
   * `catalog.manage`, so the button being enabled is a hint, not the gate.
   */
  function toggle() {
    productsCollection.update(row.id, (draft) => {
      draft.status = draft.status === 'active' ? 'draft' : 'active';
    });
  }

  return (
    <Button variant="outline" size="sm" onClick={toggle} disabled={!canEdit}>
      {row.status === 'active' ? 'Unpublish' : 'Publish'}
    </Button>
  );
}

function formatPrice(cents: number | null): string {
  if (cents === null || cents === undefined) return '—';

  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '—';

  return `${value}%`;
}
